import { CardBody } from '@statsbomb/kitbag-components';
import { PitchViz } from '@statsbomb/kitbag-datavis';
import { useAtomValue } from 'jotai';
import { ReactNode } from 'react';
import { showLegendAtom } from '@/atoms/visualisation';
import { useCurrentlySelectedVideoId } from '@/hooks/useCurrentlySelectedVideoId';
import { useGetPitchRotation } from '@/hooks/useGetPitchRotation';
import { useIsVideoPreviewOpen } from '@/hooks/useIsVideoPreviewOpen';
import { useLoadableStatus } from '@/hooks/useLoadableStatus';
import { usePitchCrop } from '@/hooks/usePitchCrop';
import { useStatus } from '@/hooks/useStatus';
import { EventMapBaseProps, EventMapProps } from '@/types/visualisation';
import { isShotEvent } from '@/utils/visualisations';
import { CardHeightWrapper } from '../card/CardHeightWrapper';
import { ErrorCard } from '../contentState/ErrorCard';
import { EventMapLayers } from './EventMap/EventMapLayers';
import { EventMapPreview } from './EventMap/EventMapPreview';
import { EventMapSelectionLayer } from './EventMap/EventMapSelectionLayer';
import { PitchVisWithAttackingDirectionArrow } from './PitchVisualisation/PitchVisWithAttackingDirectionArrow';
import { PitchVisWithLegend } from './PitchVisualisation/PitchVisWrapper';

const EventMapVis = ({ children, hasShotMarkers }: { children: ReactNode; hasShotMarkers?: boolean }) => {
  const pitchRotation = useGetPitchRotation(false);
  const showLegend = useAtomValue(showLegendAtom);

  return (
    <PitchVisWithLegend hasShotMarkers={hasShotMarkers}>
      {/* Arrow direction is shown and hidden with the legend */}
      {showLegend ? (
        <PitchVisWithAttackingDirectionArrow pitchVisType="eventmap" pitchRotation={pitchRotation}>
          {children}
        </PitchVisWithAttackingDirectionArrow>
      ) : (
        children
      )}
    </PitchVisWithLegend>
  );
};

const EventMapBase = ({
  eventMapRef,
  currentlySelectedVideoId,
  allowInteraction,
  unwrappedPitchEventsAtom,
  swapCropUnderAgainst,
}: EventMapBaseProps & { eventMapRef: React.Ref<HTMLDivElement>; currentlySelectedVideoId: string | null }) => {
  const pitchRotation = useGetPitchRotation(false);
  const visibleArea = usePitchCrop(false, swapCropUnderAgainst);
  const unwrappedPitchEvents = useAtomValue(unwrappedPitchEventsAtom);

  const hasShotMarkers = unwrappedPitchEvents.markerEvents.some(isShotEvent);

  const eventMapLayers = (
    <EventMapLayers
      currentlySelectedVideoId={currentlySelectedVideoId}
      markerEvents={unwrappedPitchEvents.markerEvents}
      arrowEvents={unwrappedPitchEvents.arrowEvents}
      allowInteraction={allowInteraction}
    />
  );

  return (
    <div ref={eventMapRef}>
      <EventMapVis hasShotMarkers={hasShotMarkers}>
        <PitchViz
          /* this is intentionally empty to overwrite pitch viz svg styles
to position the arrow correctly */
          className=""
          rotationName={pitchRotation}
          pitchFocusZoneName={visibleArea}
          overlay={
            allowInteraction ? (
              <EventMapSelectionLayer unwrappedPitchEventsAtom={unwrappedPitchEventsAtom}>
                {eventMapLayers}
              </EventMapSelectionLayer>
            ) : (
              eventMapLayers
            )
          }
        />
      </EventMapVis>
    </div>
  );
};

const EventMapWithData = ({
  pitchEventsAtom,
  unwrappedPitchEventsAtom,
  allowInteraction,
  isPreview,
  swapCropUnderAgainst,
}: EventMapProps) => {
  const status = useLoadableStatus(pitchEventsAtom);
  const statusProps = useStatus('general', status);
  const { ref, currentlySelectedVideoId } = useCurrentlySelectedVideoId();

  const isVideoPreviewOpen = useIsVideoPreviewOpen();
  const highlightVideoId = isVideoPreviewOpen ? currentlySelectedVideoId : null;

  return (
    <CardHeightWrapper>
      <CardBody {...statusProps}>
        {!isPreview ? (
          <EventMapBase
            eventMapRef={ref}
            currentlySelectedVideoId={highlightVideoId}
            unwrappedPitchEventsAtom={unwrappedPitchEventsAtom}
            allowInteraction={allowInteraction}
            swapCropUnderAgainst={swapCropUnderAgainst}
          />
        ) : (
          <EventMapPreview
            currentlySelectedVideoId={highlightVideoId}
            unwrappedPitchEventsAtom={unwrappedPitchEventsAtom}
          />
        )}
      </CardBody>
    </CardHeightWrapper>
  );
};

export const EventMap = (props: EventMapProps) => {
  const pitchRotation = useGetPitchRotation(false);
  const visibleArea = usePitchCrop(false);

  return (
    <ErrorCard
      translationNameSpace="general"
      fallbackChildren={
        <EventMapVis>
          <PitchViz
            /* this is intentionally empty to overwrite pitch viz svg styles
      to position the arrow correctly */
            className=""
            rotationName={pitchRotation}
            pitchFocusZoneName={visibleArea}
            overlay={<EventMapLayers currentlySelectedVideoId={null} markerEvents={[]} arrowEvents={[]} />}
          />
        </EventMapVis>
      }
    >
      <EventMapWithData {...props} />
    </ErrorCard>
  );
};
